import React, { useState } from "react";
import { Users, Layers, AlertTriangle, ZoomIn, Maximize2 } from "lucide-react";
import { useTranslate } from "../../i18n/useTranslate";

const zones = [
  { id:1, name:"Ramkund Ghat",      x:46, y:41, risk:"critical", crowd:"92%", count:"92,140", type:"Main Snan Ghat"    },
  { id:2, name:"Panchvati",         x:32, y:54, risk:"high",     crowd:"81%", count:"81,200", type:"Pilgrim Zone"      },
  { id:3, name:"Trimbakeshwar",     x:16, y:30, risk:"high",     crowd:"76%", count:"76,400", type:"Temple Town"       },
  { id:4, name:"Nashik Road Stn",   x:70, y:66, risk:"moderate", crowd:"68%", count:"68,100", type:"Transport Hub"     },
  { id:5, name:"Kalaram Temple",    x:54, y:61, risk:"moderate", crowd:"58%", count:"58,200", type:"Temple Zone"       },
  { id:6, name:"Gangapur Camp",     x:80, y:44, risk:"normal",   crowd:"41%", count:"41,800", type:"Staging Camp"      },
];

const heatColors = {
  critical: { ring:"rgba(239,68,68,0.35)",  dot:"#EF4444", label:"bg-red-600",    text:"text-white",   border:"border-red-300"    },
  high:     { ring:"rgba(249,115,22,0.30)", dot:"#F97316", label:"bg-orange-500", text:"text-white",   border:"border-orange-300" },
  moderate: { ring:"rgba(37,99,235,0.25)",  dot:"#2563EB", label:"bg-blue-600",   text:"text-white",   border:"border-blue-300"   },
  normal:   { ring:"rgba(16,185,129,0.25)", dot:"#10B981", label:"bg-emerald-500",text:"text-white",   border:"border-emerald-300"},
};

export default function MapPlaceholder({ height = 340, showHeatmap = true }) {
  const t = useTranslate();
  const [hovered, setHovered] = useState(null);
  const [layer, setLayer] = useState("crowd");

  return (
    <div className="relative w-full rounded-2xl overflow-hidden border border-slate-200 bg-gradient-to-br from-blue-50/80 via-slate-50 to-indigo-50/60" style={{ height }}>

      {/* Grid */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.07]">
        <defs>
          <pattern id="mapgrid" width="32" height="32" patternUnits="userSpaceOnUse">
            <path d="M 32 0 L 0 0 0 32" fill="none" stroke="#1E40AF" strokeWidth="0.8" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#mapgrid)" />
      </svg>

      {/* Topographic lines */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.06]">
        <ellipse cx="35%" cy="50%" rx="22%" ry="14%" fill="none" stroke="#1D4ED8" strokeWidth="1.5" />
        <ellipse cx="35%" cy="50%" rx="16%" ry="10%" fill="none" stroke="#1D4ED8" strokeWidth="1.5" />
        <ellipse cx="48%" cy="42%" rx="18%" ry="11%" fill="none" stroke="#1D4ED8" strokeWidth="1.5" />
        <ellipse cx="48%" cy="42%" rx="12%" ry="7%"  fill="none" stroke="#1D4ED8" strokeWidth="1.5" />
        <ellipse cx="72%" cy="55%" rx="16%" ry="12%" fill="none" stroke="#1D4ED8" strokeWidth="1.5" />
      </svg>

      {/* Heatmap overlay */}
      {showHeatmap && (
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none transition-opacity duration-300"
          style={{ opacity: layer === "zones" ? 0.12 : 1 }}
        >
          <defs>
            <radialGradient id="h1" cx="50%" cy="50%" r="50%">
              <stop offset="0%"   stopColor="#EF4444" stopOpacity="0.28" />
              <stop offset="100%" stopColor="#EF4444" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="h2" cx="50%" cy="50%" r="50%">
              <stop offset="0%"   stopColor="#F97316" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#F97316" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="h3" cx="50%" cy="50%" r="50%">
              <stop offset="0%"   stopColor="#F97316" stopOpacity="0.20" />
              <stop offset="100%" stopColor="#F97316" stopOpacity="0" />
            </radialGradient>
          </defs>
          <ellipse cx="46%" cy="41%" rx="10%" ry="8%"  fill="url(#h1)" />
          <ellipse cx="32%" cy="54%" rx="9%"  ry="7%"  fill="url(#h2)" />
          <ellipse cx="16%" cy="30%" rx="8%"  ry="7%"  fill="url(#h3)" />
        </svg>
      )}

      {/* Godavari River */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          <linearGradient id="riverGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor="#93C5FD" stopOpacity="0.9" />
            <stop offset="50%"  stopColor="#60A5FA" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#93C5FD" stopOpacity="0.9" />
          </linearGradient>
        </defs>
        {/* Main Godavari */}
        <path d="M 0,46% Q 15%,42%, 30%,42%, 50%,44%, 70%,48%, 87%,50%, 100%,52%"
          stroke="url(#riverGrad)" strokeWidth="9" fill="none" strokeLinecap="round" />
        {/* Shimmer */}
        <path d="M 0,47% Q 15%,43%, 30%,43%, 50%,45%, 70%,49%, 87%,51%, 100%,53%"
          stroke="white" strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.5" />
        {/* Tributary → Trimbakeshwar */}
        <path d="M 16%,30% Q 20%,36%, 24%,42%, 30%,42%"
          stroke="url(#riverGrad)" strokeWidth="5" fill="none" strokeLinecap="round" />
      </svg>

      {/* River label */}
      <div className="absolute pointer-events-none select-none" style={{ top:"43%", left:"36%", transform:"rotate(-2deg)" }}>
        <span className="text-[11px] font-black text-blue-500/60 italic">{t("Godavari River")}</span>
      </div>

      {/* Zone markers */}
      {zones.map(z => {
        const c   = heatColors[z.risk];
        const isH = hovered === z.id;
        // Layer-driven emphasis: "heat" de-emphasizes markers in favor of the
        // heatmap blobs; "zones" emphasizes markers with a boundary ring;
        // "crowd" (default) renders exactly as before.
        const markerScale   = layer === "heat" ? 0.85 : layer === "zones" ? 1.15 : 1;
        const markerOpacity = layer === "heat" ? 0.55 : 1;
        return (
          <div
            key={z.id}
            className="absolute cursor-pointer transition-opacity duration-300"
            style={{
              left:`${z.x}%`, top:`${z.y}%`,
              transform:`translate(-50%,-50%) scale(${markerScale})`,
              opacity: markerOpacity,
            }}
            onMouseEnter={() => setHovered(z.id)}
            onMouseLeave={() => setHovered(null)}
          >
            {/* Pulse rings for critical/high */}
            {(z.risk === "critical" || z.risk === "high") && (
              <>
                <span className="absolute -inset-3 rounded-full animate-ping-slow opacity-30"
                  style={{ background: c.ring }} />
                <span className="absolute -inset-1.5 rounded-full opacity-20"
                  style={{ background: c.ring }} />
              </>
            )}

            {/* Zone boundary ring - emphasized only on the "zones" layer */}
            {layer === "zones" && (
              <span className="absolute -inset-4 rounded-full border-2 border-dashed opacity-70"
                style={{ borderColor: c.dot }} />
            )}

            {/* Dot */}
            <div
              className={`relative w-4 h-4 rounded-full border-2 border-white shadow-md transition-transform duration-200 ${isH ? "scale-150" : "hover:scale-125"}`}
              style={{ backgroundColor: c.dot }}
            >
              {z.risk === "critical" && (
                <span className="absolute inset-0 flex items-center justify-center text-white text-[7px] font-black">!</span>
              )}
            </div>

            {/* Label tooltip */}
            <div className={`absolute left-5 top-1/2 -translate-y-1/2 whitespace-nowrap transition-all duration-150 ${isH ? "opacity-100 scale-100" : "opacity-90 scale-95"}`}>
              <div className={`${c.label} ${c.text} text-[10px] font-bold px-2 py-1 rounded-lg shadow-md`}>
                {t(z.name)}
                <span className="ml-1 opacity-80">{z.crowd}</span>
              </div>
              {isH && (
                <div className="bg-white/95 border border-slate-200 text-[9px] text-slate-600 px-2 py-1 rounded-lg shadow mt-0.5">
                  <div className="font-bold text-slate-800">{z.count} {t("pilgrims")}</div>
                  <div>{t(z.type)}</div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Layer controls */}
      <div className="absolute top-3 right-3 flex flex-col gap-1.5">
        <div className="bg-white/95 border border-slate-200 rounded-xl p-1.5 shadow-sm flex gap-1">
          {[{k:"crowd",label:"Crowd"},{k:"heat",label:"Heat"},{k:"zones",label:"Zones"}].map(({k:l, label}) => (
            <button key={l}
              onClick={() => setLayer(l)}
              className={`text-[9px] font-black px-2 py-1 rounded-lg transition-colors ${
                layer === l ? "bg-blue-700 text-white" : "text-slate-500 hover:bg-slate-100"
              }`}
            >
              {t(label)}
            </button>
          ))}
        </div>
      </div>

      {/* Header badge */}
      <div className="absolute top-3 left-1/2 -translate-x-1/2">
        <span className="gov-gradient text-[#0A2A5E] text-[10px] font-black px-3 py-1 rounded-full shadow whitespace-nowrap">
          {t("Nashik · Simhastha 2027 — Live GIS View")}
        </span>
      </div>

      {/* Pilgrim count */}
      <div className="absolute bottom-3 right-3 bg-white/95 border border-slate-200 rounded-xl px-3 py-2 shadow-sm">
        <div className="flex items-center gap-1.5">
          <Users size={12} className="text-blue-700" />
          <span className="text-xs font-black text-slate-800">6,84,210</span>
        </div>
        <div className="text-[9px] text-slate-400 mt-0.5">{t("Active pilgrims")}</div>
      </div>

      {/* Legend */}
      <div className="absolute bottom-3 left-3 bg-white/95 border border-slate-200 rounded-xl px-2.5 py-2 shadow-sm">
        <div className="text-[8px] font-black text-slate-400 uppercase tracking-wider mb-1.5">{t("Risk Level")}</div>
        {Object.entries({ critical:"Critical", high:"High", moderate:"Moderate", normal:"Normal" }).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1.5 mb-0.5">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: heatColors[k].dot }} />
            <span className="text-[9px] font-semibold text-slate-600">{t(v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
